/**
 * Маржинальность: по направлениям, номенклатурным группам и филиалам,
 * плюс ABC клиентов. Строка открывает детализацию — переход вниз по
 * следующему измерению с фильтром по выбранной строке.
 */

import { useEffect, useState, type CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useFinance } from '../context'
import { indexBy, num, rowKey, useSettled } from '../data'
import { BarRows } from '../charts'
import { Caveat, DataNotes, Delta, Panel, Segmented, SkeletonRows } from '../parts'
import { originFrom } from '../Drawer'
import { useCustomers, useSales, useSalesSource } from '../queries'
import { compareLabel } from '../period'
import { salesEmpty } from '../frame'
import { DIMENSION_TITLES, money } from './Overview'
import { usePublishFreshness } from '../freshness'
import { deltaPoints, formatMoneyShort, formatNumber, formatPercent, pluralize, toNumber } from '../../format'

const ABC = [
  { code: 'A', limit: 80, title: 'дают первые 80% выручки' },
  { code: 'B', limit: 95, title: 'следующие 15%' },
  { code: 'C', limit: 100, title: 'оставшиеся 5%' },
] as const

export function Margin() {
  const ctx = useFinance()
  const { range, previousRange, selection, branch, drill, catalog, noCompare } = ctx
  const [params, setParams] = useSearchParams()
  const source = useSalesSource()
  const withMargin = source?.withMargin ?? false
  const requested = params.get('d')
  const dimension = requested && ['direction', 'nomenclature_group', 'branch'].includes(requested) && !(branch && requested === 'branch') ? requested : 'nomenclature_group'

  const currentLive = useSales(dimension, range)
  const beforeLive = useSales(dimension, previousRange)
  const customers = useCustomers(range)
  usePublishFreshness([currentLive, customers])

  // Маржа и её изменение — из одной пары ответов, вместе с разрезом, к которому они относятся.
  const split = useSettled([currentLive, beforeLive], { dimension, noCompare, selection })
  const [current, before] = split.states
  const shown = split.view.dimension
  const incomplete = Boolean(split.view.noCompare)

  const rows = [...(current.data?.rows ?? [])].filter((row) => num(row.revenue) !== 0).sort((a, b) => num(b.revenue) - num(a.revenue))
  const previousByKey = indexBy(before.data?.rows, shown)
  const avg = toNumber(current.data?.totals.margin_percent)
  const revenueTotal = num(current.data?.totals.revenue)
  const [order, setOrder] = useState<'revenue' | 'margin'>('margin')
  const ranked = withMargin && order === 'margin' ? [...rows].sort((a, b) => num(b.margin_percent) - num(a.margin_percent)) : rows

  const setDimension = (value: string) =>
    setParams((currentParams) => {
      const next = new URLSearchParams(currentParams)
      next.set('d', value)
      return next
    }, { replace: true })

  // Переход из сигнала обзора: открыть детализацию нужной строки.
  const openValue = params.get('o')
  useEffect(() => {
    if (!openValue || current.status !== 'ready' || split.pending || shown !== dimension) return
    const row = rows.find((item) => rowKey(item, dimension) === openValue)
    if (row) drill({ kind: 'sales', dimension, value: openValue, label: String(row[dimension] ?? '—') }, { element: null, rect: null })
    setParams((currentParams) => {
      const next = new URLSearchParams(currentParams)
      next.delete('o')
      return next
    }, { replace: true })
  }, [openValue, current.status, split.pending, shown])

  // --- ABC ------------------------------------------------------------------
  const customerRows = [...(customers.data?.rows ?? [])].filter((row) => num(row.revenue) > 0).sort((a, b) => num(b.revenue) - num(a.revenue))
  const customerTotal = customerRows.reduce((sum, row) => sum + num(row.revenue), 0)
  let running = 0
  const classified = customerRows.map((row) => {
    const startShare = customerTotal > 0 ? (running / customerTotal) * 100 : 0
    running += num(row.revenue)
    const share = customerTotal > 0 ? (running / customerTotal) * 100 : 0
    // Класс по накопленной доле на начало строки: клиент, пересекающий порог 80%, ещё входит в A.
    const klass = startShare < 80 ? 'A' : startShare < 95 ? 'B' : 'C'
    return { row, share, klass }
  })
  // Маржа клиентов — из самого ABC-отчёта: она может быть выдана и без отчёта по филиалам.
  const abcMargin = customers.data?.columns.some((column) => column.key === 'margin_percent') ?? false
  const classCount = (code: string) => classified.filter((item) => item.klass === code).length

  return (
    <div className="fin-stack">
      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}
      {!withMargin && source && (
        <Caveat tone="warn">Маржа не входит в ваши права — показана только выручка без НДС.</Caveat>
      )}

      <Panel
        index={0}
        title={withMargin ? 'Рентабельность' : 'Выручка'}
        subtitle={
          withMargin
            ? `Маржа = валовая прибыль ÷ выручка с себестоимостью · линия — среднее (${formatPercent(avg)})`
            : 'Без НДС, за выбранный период'
        }
        states={[current]}
        skeleton={<SkeletonRows rows={6} />}
        empty={salesEmpty(ctx, rows.length === 0)}
        actions={
          <div className="row row--tight">
            {withMargin && (
              <Segmented
                size="sm"
                label="Порядок"
                value={order}
                onChange={setOrder}
                options={[
                  { value: 'margin', label: 'По марже' },
                  { value: 'revenue', label: 'По выручке' },
                ]}
              />
            )}
            <Segmented
              size="sm"
              label="Разрез"
              value={dimension}
              onChange={setDimension}
              options={[
                { value: 'direction', label: DIMENSION_TITLES.direction! },
                { value: 'nomenclature_group', label: DIMENSION_TITLES.nomenclature_group! },
                ...(branch ? [] : [{ value: 'branch', label: DIMENSION_TITLES.branch! }]),
              ]}
            />
          </div>
        }
      >
        {() =>
          withMargin ? (
            <BarRows
              ariaLabel="Рентабельность по разрезу"
              series={[{ key: 'margin', label: 'Маржа', color: 'var(--series-3)' }]}
              max={Math.max(1, ...ranked.map((row) => Math.abs(num(row.margin_percent))))}
              reference={avg !== null ? { value: Math.max(0, avg), label: `среднее ${formatPercent(avg)}` } : undefined}
              valueWidth={70}
              rows={ranked.map((row) => {
                const key = rowKey(row, shown)
                return {
                  key,
                  label: String(row[shown] ?? '—'),
                  sub: `выручка ${money(num(row.revenue))} · ${revenueTotal > 0 ? formatPercent((num(row.revenue) / revenueTotal) * 100) : '—'}`,
                  values: [toNumber(row.margin_percent)],
                  format: (value: number) => formatPercent(value),
                  aside: incomplete ? null : (
                    <Delta value={deltaPoints(row.margin_percent, previousByKey.get(key)?.margin_percent)} points />
                  ),
                }
              })}
              onSelect={(row, element) => drill({ kind: 'sales', dimension: shown, value: row.key, label: row.label }, originFrom(element))}
            />
          ) : (
            <BarRows
              ariaLabel="Выручка по разрезу"
              series={[{ key: 'revenue', label: 'Выручка', color: 'var(--series-1)' }]}
              rows={rows.map((row) => ({
                key: rowKey(row, shown),
                label: String(row[shown] ?? '—'),
                values: [num(row.revenue)],
              }))}
              onSelect={(row, element) => drill({ kind: 'sales', dimension: shown, value: row.key, label: row.label }, originFrom(element))}
            />
          )
        }
      </Panel>
      {!incomplete && withMargin && <div className="tiny muted" style={{ marginTop: -10 }}>Справа — изменение маржи {compareLabel(split.view.selection)}, в процентных пунктах.</div>}

      {catalog.has('abc_customers') ? (
        <Panel
          index={1}
          title="ABC-анализ клиентов"
          subtitle="Клиенты по убыванию выручки; класс — по накопленной доле выручки"
          states={[customers]}
          skeleton={<SkeletonRows rows={8} height={16} />}
          empty={salesEmpty(ctx, customerRows.length === 0, 'Продаж клиентам за период нет')}
        >
          {() => (
            <>
              <div className="abc-summary">
                {ABC.map((item, index) => (
                  <div key={item.code} className={`abc-chip abc-chip--${item.code} cascade`} style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                    <span className="abc-chip__code">{item.code}</span>
                    <span>
                      <span className="strong">{pluralize(classCount(item.code), 'клиент', 'клиента', 'клиентов')}</span>
                      <span className="tiny muted" style={{ display: 'block' }}>{item.title}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="table-wrap">
                <table className="fin-table fin-table--interactive">
                  <thead>
                    <tr>
                      <th>Класс</th>
                      <th>Клиент</th>
                      <th className="num">Выручка</th>
                      <th className="num">Накопленная доля</th>
                      {abcMargin && <th className="num">Валовая прибыль</th>}
                      {abcMargin && <th className="num">Маржа</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {classified.slice(0, 15).map(({ row, share, klass }, index) => {
                      const key = rowKey(row, 'counterparty')
                      const label = String(row.counterparty ?? '—')
                      const open = (element: HTMLElement) => drill({ kind: 'customer', value: key, label }, originFrom(element))
                      return (
                        <tr
                          key={key}
                          className="cascade"
                          style={{ '--i': Math.min(index, 7) } as CSSProperties}
                          tabIndex={0}
                          onClick={(event) => open(event.currentTarget)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              open(event.currentTarget)
                            }
                          }}
                        >
                          <td>
                            <span className={`abc-badge abc-badge--${klass}`}>{klass}</span>
                          </td>
                          <td className="strong">{label}</td>
                          <td className="num mono">{formatMoneyShort(row.revenue)}</td>
                          <td className="num mono muted">{formatNumber(Math.round(share * 10) / 10)}%</td>
                          {abcMargin && <td className={`num mono${num(row.gross_profit) < 0 ? ' cell-negative' : ''}`}>{formatMoneyShort(row.gross_profit)}</td>}
                          {abcMargin && <td className="num mono">{formatPercent(row.margin_percent)}</td>}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {classified.length > 15 && (
                <div className="tiny muted fin-footnote--in-panel">
                  Показаны 15 крупнейших из {classified.length}. Полный список — в отчёте «ABC-анализ клиентов».
                </div>
              )}
            </>
          )}
        </Panel>
      ) : null}

      <DataNotes states={[current, customers]} />
    </div>
  )
}
